import {useEffect, useState} from "react";
import {css} from "@panda/css";
import {Formik} from "formik";
import * as Yup from "yup";

import {Button} from "@pg-design/button-module";
import {CarOutlineIcon, PoiBusIcon, WalkIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {countDistance} from "@pg-mono/geo-utils";
import {convertToLatLngLiteralOfPoland} from "@pg-mono/open-street-map";

import {fetchTravelDirections} from "../../../maps/actions/fetch_travel_directions";
import {PlacesAutocomplete} from "../../../maps/components/PlacesAutocomplete";
import {setPendingSinglePoi} from "../../../maps/state/pending_single_poi_slice";
import {isValidLocationObject} from "../../../maps/utils/is_valid_location_object";
import {useAppDispatch} from "../../../store/hooks";
import {propertyFaqAnalytics} from "../../tracking/property_faq_tracking";
import {formatDuration, TravelMode} from "../../utils/TravelMode";
import {OSM_MAP_CONTAINER} from "../PropertyDetailForSale";
import {PropertyDetailFaqAnswerGate} from "./PropertyDetailFaqAnswerGate";

interface IProps {
    geoPointCoordinates: [number, number];
    intro?: string;
    onOpenMapModal?: () => void;
    canSeeAnswer: boolean;
    propertyId: number;
    offerId: number;
    questionHeading: string;
    questionIndex: number;
}

interface ISubmittedLocation {
    label: string;
    coordinates: number[];
}

interface ICommuteResult {
    address: string;
    destination: {lat: number; lng: number};
    distanceMeters: number;
    durations: {mode: TravelMode; value: number | null}[];
}

const TRAVEL_MODES: {mode: TravelMode; label: string; Icon: typeof CarOutlineIcon}[] = [
    {mode: TravelMode.TRANSIT, label: "Komunikacją", Icon: PoiBusIcon},
    {mode: TravelMode.DRIVING, label: "Samochodem", Icon: CarOutlineIcon},
    {mode: TravelMode.WALKING, label: "Pieszo", Icon: WalkIcon}
];

const initialValues = {
    location: {label: "", value: "" as unknown as number, coordinates: [] as number[]}
};

const validationSchema = Yup.object({
    location: Yup.mixed().test({message: "Wpisz adres", test: (value) => isValidLocationObject(value)})
});

const formatKm = (meters: number) => `${(meters / 1000).toFixed(1).replace(".", ",")} km`;

const scrollToMap = () => {
    if (typeof document === "undefined") {
        return;
    }
    document.querySelector(`[data-id="${OSM_MAP_CONTAINER}"]`)?.scrollIntoView({behavior: "smooth", block: "center"});
};

export const PropertyDetailFaqCommute = (props: IProps) => {
    const {geoPointCoordinates, intro, onOpenMapModal, canSeeAnswer, propertyId, offerId, questionHeading, questionIndex} = props;
    const dispatch = useAppDispatch();
    const [result, setResult] = useState<ICommuteResult | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [gatedSubmission, setGatedSubmission] = useState<ISubmittedLocation | null>(null);

    const computeResult = async (location: ISubmittedLocation) => {
        setIsLoading(true);

        try {
            const destination = convertToLatLngLiteralOfPoland(location.coordinates);
            const origin = convertToLatLngLiteralOfPoland(geoPointCoordinates);
            const distanceMeters = countDistance(destination, origin);

            const durations = await Promise.all(
                TRAVEL_MODES.map(async ({mode}) => {
                    const response = await fetchTravelDirections({origin, destination, travelMode: mode});

                    return {mode, value: response?.value ?? null};
                })
            );

            setResult({address: location.label, destination, distanceMeters, durations});
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (values: typeof initialValues) => {
        const location = {label: values.location.label || "", coordinates: values.location.coordinates};

        // Non-verified user: show the registration gate instead of the result — the (expensive) Google
        // Directions calls run only for verified users
        if (!canSeeAnswer) {
            setGatedSubmission(location);
            return;
        }

        await computeResult(location);
    };

    useEffect(() => {
        // The user verified their phone after submitting — compute the queued result automatically
        if (canSeeAnswer && gatedSubmission && !result && !isLoading) {
            setGatedSubmission(null);
            void computeResult(gatedSubmission);
        }
    }, [canSeeAnswer]);

    const onSeeOnMap = () => {
        if (!result) {
            return;
        }

        propertyFaqAnalytics.amplitude.seeOnMapClick({
            index: questionIndex,
            itemId: offerId,
            propertyId,
            questionHeading,
            questionType: "default"
        });

        if (onOpenMapModal) {
            dispatch(
                setPendingSinglePoi({
                    id: Date.now(),
                    name: "Moje miejsce",
                    distance: result.distanceMeters,
                    lat: result.destination.lat,
                    lng: result.destination.lng,
                    tags: {address: result.address}
                })
            );
            onOpenMapModal();
            return;
        }

        scrollToMap();
    };

    // Submitted while non-verified → the address is queued and the registration gate replaces the result
    if (gatedSubmission && !canSeeAnswer) {
        return <PropertyDetailFaqAnswerGate propertyId={propertyId} offerId={offerId} />;
    }

    if (result) {
        return (
            <div className={resultStyle}>
                {intro && (
                    <Text variant="body_copy_2" as="p">
                        {intro}
                    </Text>
                )}

                <Text variant="body_copy_2" as="p">
                    Odległość do Twojego miejsca <strong>wynosi {formatKm(result.distanceMeters)}</strong>, oznacza to, że czas dojazdu wyniesie:
                </Text>

                <div className={modesStyle}>
                    {result.durations.map(({mode, value}) => {
                        const meta = TRAVEL_MODES.find((travelMode) => travelMode.mode === mode);

                        if (!meta) {
                            return null;
                        }

                        return (
                            <div key={mode} className={modeRowStyle}>
                                <meta.Icon size="2.4" />
                                <Text variant="body_copy_2" as="span">
                                    {meta.label}: <strong>{value != null ? formatDuration(value) : "brak danych"}</strong>
                                </Text>
                            </div>
                        );
                    })}
                </div>

                <div className={actionsStyle}>
                    <Button variant="filled_secondary" size="small" onClick={onSeeOnMap}>
                        Zobacz na mapie
                    </Button>

                    <Button variant="outlined_secondary" size="small" onClick={() => setResult(null)}>
                        Zmień adres
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <Formik initialValues={{...initialValues}} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize>
            {(formik) => (
                <form onSubmit={formik.handleSubmit} className={formStyle}>
                    <PlacesAutocomplete
                        name="location"
                        placeholder="Wpisz adres"
                        allowEditLastValue
                        disableDropdownIndicator
                        portalMenu
                        className={inputStyle}
                    />

                    <Button
                        type="submit"
                        variant="outlined_secondary"
                        size="small"
                        disabled={isLoading}
                        isLoading={isLoading}
                        className={submitStyle}
                        onClick={() => {
                            propertyFaqAnalytics.amplitude.confirmAddressClick({
                                index: questionIndex,
                                itemId: offerId,
                                propertyId,
                                questionHeading,
                                questionType: "default"
                            });
                        }}
                    >
                        Zatwierdź
                    </Button>
                </form>
            )}
        </Formik>
    );
};

const formStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    width: "100%",

    md: {
        flexDirection: "row",
        alignItems: "flex-start"
    }
});

const inputStyle = css({
    flex: 1,
    minWidth: 0
});

const submitStyle = css({
    flexShrink: 0,

    md: {
        width: "20rem"
    }
});

const resultStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2
});

const modesStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1
});

const modeRowStyle = css({
    display: "flex",
    alignItems: "center",
    gap: 1
});

const actionsStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1,

    md: {
        flexDirection: "row",
        gap: 2
    }
});
import {useEffect, useState} from "react";
import {css} from "@panda/css";
import {Formik} from "formik";
import * as Yup from "yup";

import {Button} from "@pg-design/button-module";
import {CarOutlineIcon, PoiBusIcon, WalkIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {countDistance} from "@pg-mono/geo-utils";
import {convertToLatLngLiteralOfPoland} from "@pg-mono/open-street-map";

import {fetchTravelDirections} from "../../../maps/actions/fetch_travel_directions";
import {PlacesAutocomplete} from "../../../maps/components/PlacesAutocomplete";
import {setPendingSinglePoi} from "../../../maps/state/pending_single_poi_slice";
import {isValidLocationObject} from "../../../maps/utils/is_valid_location_object";
import {useAppDispatch} from "../../../store/hooks";
import {propertyFaqAnalytics} from "../../tracking/property_faq_tracking";
import {formatDuration, TravelMode} from "../../utils/TravelMode";
import {OSM_MAP_CONTAINER} from "../PropertyDetailForSale";
import {PropertyDetailFaqAnswerGate} from "./PropertyDetailFaqAnswerGate";

interface IProps {
    geoPointCoordinates: [number, number];
    intro?: string;
    onOpenMapModal?: () => void;
    canSeeAnswer: boolean;
    propertyId: number;
    offerId: number;
    questionHeading: string;
    questionIndex: number;
}

interface ISubmittedLocation {
    label: string;
    coordinates: number[];
}

interface ICommuteResult {
    address: string;
    destination: {lat: number; lng: number};
    distanceMeters: number;
    durations: {mode: TravelMode; value: number | null}[];
}

const TRAVEL_MODES: {mode: TravelMode; label: string; Icon: typeof CarOutlineIcon}[] = [
    {mode: TravelMode.TRANSIT, label: "Komunikacją", Icon: PoiBusIcon},
    {mode: TravelMode.DRIVING, label: "Samochodem", Icon: CarOutlineIcon},
    {mode: TravelMode.WALKING, label: "Pieszo", Icon: WalkIcon}
];

const initialValues = {
    location: {label: "", value: "" as unknown as number, coordinates: [] as number[]}
};

const validationSchema = Yup.object({
    location: Yup.mixed().test({message: "Wpisz adres", test: (value) => isValidLocationObject(value)})
});

const formatKm = (meters: number) => `${(meters / 1000).toFixed(1).replace(".", ",")} km`;

const scrollToMap = () => {
    if (typeof document === "undefined") {
        return;
    }
    document.querySelector(`[data-id="${OSM_MAP_CONTAINER}"]`)?.scrollIntoView({behavior: "smooth", block: "center"});
};

export const PropertyDetailFaqCommute = (props: IProps) => {
    const {geoPointCoordinates, intro, onOpenMapModal, canSeeAnswer, propertyId, offerId, questionHeading, questionIndex} = props;
    const dispatch = useAppDispatch();
    const [result, setResult] = useState<ICommuteResult | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [gatedSubmission, setGatedSubmission] = useState<ISubmittedLocation | null>(null);

    const computeResult = async (location: ISubmittedLocation) => {
        setIsLoading(true);

        try {
            const destination = convertToLatLngLiteralOfPoland(location.coordinates);
            const origin = convertToLatLngLiteralOfPoland(geoPointCoordinates);
            const distanceMeters = countDistance(destination, origin);

            const durations = await Promise.all(
                TRAVEL_MODES.map(async ({mode}) => {
                    const response = await fetchTravelDirections({origin, destination, travelMode: mode});

                    return {mode, value: response?.value ?? null};
                })
            );

            setResult({address: location.label, destination, distanceMeters, durations});
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (values: typeof initialValues) => {
        const location = {label: values.location.label || "", coordinates: values.location.coordinates};

        // Non-verified user: show the registration gate instead of the result — the (expensive) Google
        // Directions calls run only for verified users
        if (!canSeeAnswer) {
            setGatedSubmission(location);
            return;
        }

        await computeResult(location);
    };

    useEffect(() => {
        // The user verified their phone after submitting — compute the queued result automatically
        if (canSeeAnswer && gatedSubmission && !result && !isLoading) {
            setGatedSubmission(null);
            void computeResult(gatedSubmission);
        }
    }, [canSeeAnswer]);

    const onSeeOnMap = () => {
        if (!result) {
            return;
        }

        propertyFaqAnalytics.amplitude.seeOnMapClick({
            index: questionIndex,
            itemId: offerId,
            propertyId,
            questionHeading,
            questionType: "default"
        });

        if (onOpenMapModal) {
            dispatch(
                setPendingSinglePoi({
                    id: Date.now(),
                    name: "Moje miejsce",
                    distance: result.distanceMeters,
                    lat: result.destination.lat,
                    lng: result.destination.lng,
                    tags: {address: result.address}
                })
            );
            onOpenMapModal();
            return;
        }

        scrollToMap();
    };

    // Submitted while non-verified → the address is queued and the registration gate replaces the result
    if (gatedSubmission && !canSeeAnswer) {
        return <PropertyDetailFaqAnswerGate propertyId={propertyId} offerId={offerId} />;
    }

    if (result) {
        return (
            <div className={resultStyle}>
                {intro && (
                    <Text variant="body_copy_2" as="p">
                        {intro}
                    </Text>
                )}

                <Text variant="body_copy_2" as="p">
                    Odległość do Twojego miejsca <strong>wynosi {formatKm(result.distanceMeters)}</strong>, oznacza to, że czas dojazdu wyniesie:
                </Text>

                <div className={modesStyle}>
                    {result.durations.map(({mode, value}) => {
                        const meta = TRAVEL_MODES.find((travelMode) => travelMode.mode === mode);

                        if (!meta) {
                            return null;
                        }

                        return (
                            <div key={mode} className={modeRowStyle}>
                                <meta.Icon size="2.4" />
                                <Text variant="body_copy_2" as="span">
                                    {meta.label}: <strong>{value != null ? formatDuration(value) : "brak danych"}</strong>
                                </Text>
                            </div>
                        );
                    })}
                </div>

                <div className={actionsStyle}>
                    <Button variant="filled_secondary" size="small" onClick={onSeeOnMap}>
                        Zobacz na mapie
                    </Button>

                    <Button variant="outlined_secondary" size="small" onClick={() => setResult(null)}>
                        Zmień adres
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <Formik initialValues={{...initialValues}} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize>
            {(formik) => (
                <form onSubmit={formik.handleSubmit} className={formStyle}>
                    <PlacesAutocomplete
                        name="location"
                        placeholder="Wpisz adres"
                        allowEditLastValue
                        disableDropdownIndicator
                        portalMenu
                        className={inputStyle}
                    />

                    <Button
                        type="submit"
                        variant="outlined_secondary"
                        size="small"
                        disabled={isLoading}
                        isLoading={isLoading}
                        className={submitStyle}
                        onClick={() => {
                            propertyFaqAnalytics.amplitude.confirmAddressClick({
                                index: questionIndex,
                                itemId: offerId,
                                propertyId,
                                questionHeading,
                                questionType: "default"
                            });
                        }}
                    >
                        Zatwierdź
                    </Button>
                </form>
            )}
        </Formik>
    );
};

const formStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    width: "100%",

    md: {
        flexDirection: "row",
        alignItems: "flex-start"
    }
});

const inputStyle = css({
    flex: 1,
    minWidth: 0
});

const submitStyle = css({
    flexShrink: 0,

    md: {
        width: "20rem"
    }
});

const resultStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2
});

const modesStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1
});

const modeRowStyle = css({
    display: "flex",
    alignItems: "center",
    gap: 1
});

const actionsStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1,

    md: {
        flexDirection: "row",
        gap: 2
    }
});
